/**
 * Customer rewards wallet API.
 *
 * Endpoints (verified against /openapi.json — tag "Customer Rewards"):
 *   GET  /api/v1/customer/rewards/wallet        → available balance + recent ledger
 *   GET  /api/v1/customer/rewards/transactions  → paginated full ledger
 *   POST /api/v1/customer/rewards/redeem         → spend points on a service request
 *
 * Points are denominated 1 point = ₹1 of service-fee discount — the redeem
 * response confirms it (redeemed_points 200 → discount_amount 200.0). Redemption
 * is capped server-side at `reward_max_redemption_pct` (default 50%) of the
 * service fee AND at the wallet balance. The UI mirrors those caps for a smooth
 * experience, but the backend stays the source of truth — see BACKEND_QUIRKS.md.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { createKeyFactory, type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
import { serviceRequestKeys } from './service-requests';

// ---------- types -----------------------------------------------------------

export type RewardWalletResponse          = Schemas['RewardWalletResponse'];
export type RewardTransactionResponse     = Schemas['RewardTransactionResponse'];
export type RewardTransactionListResponse = Schemas['RewardTransactionListResponse'];
export type RewardRedeemRequest           = Schemas['RewardRedeemRequest'];
export type RewardRedeemResponse          = Schemas['RewardRedeemResponse'];

// ---------- query keys ------------------------------------------------------

export const rewardKeys = createKeyFactory('customer.rewards', {
  wallet:       () => ['wallet'] as const,
  transactions: (page: number, pageSize: number) =>
    ['transactions', page, pageSize] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/rewards/wallet */
export async function getRewardWallet(signal?: AbortSignal) {
  const { data } = await apiClient.get<RewardWalletResponse>(
    '/api/v1/customer/rewards/wallet',
    { signal },
  );
  return data;
}

/** GET /api/v1/customer/rewards/transactions?page=&page_size= */
export async function getRewardTransactions(
  page = 1,
  pageSize = 20,
  signal?: AbortSignal,
) {
  const { data } = await apiClient.get<RewardTransactionListResponse>(
    '/api/v1/customer/rewards/transactions',
    { params: { page, page_size: pageSize }, signal },
  );
  return data;
}

/** POST /api/v1/customer/rewards/redeem */
export async function redeemPoints(payload: RewardRedeemRequest) {
  const { data } = await apiClient.post<RewardRedeemResponse>(
    '/api/v1/customer/rewards/redeem',
    payload,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useRewardWallet(
  options?: Omit<UseQueryOptions<RewardWalletResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<RewardWalletResponse, ApiError>({
    queryKey: rewardKeys.wallet(),
    queryFn: ({ signal }) => getRewardWallet(signal),
    ...options,
  });
}

/**
 * Paginated ledger. `keepPreviousData` keeps the previous page visible while
 * the next page streams in, so Prev/Next don't flash an empty table.
 */
export function useRewardTransactions(
  page: number,
  pageSize = 20,
  options?: Omit<UseQueryOptions<RewardTransactionListResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<RewardTransactionListResponse, ApiError>({
    queryKey: rewardKeys.transactions(page, pageSize),
    queryFn: ({ signal }) => getRewardTransactions(page, pageSize, signal),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/**
 * Redeem points against a service request. On success we invalidate the wallet
 * and the whole transactions ledger (balance + a new debit row), plus the
 * affected service request (its fee now carries a discount).
 */
export function useRedeemPoints(
  options?: UseMutationOptions<RewardRedeemResponse, ApiError, RewardRedeemRequest>,
) {
  return useMutation<RewardRedeemResponse, ApiError, RewardRedeemRequest>({
    mutationFn: redeemPoints,
    // Spread caller options first so our cache invalidation always runs last
    // and can't be clobbered by a caller-supplied onSuccess.
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: rewardKeys.all });
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(vars.service_request_id) });
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.list() });
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}
